import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import RecipeIngredient from "./RecipeIngredient";
import useLoginCheck from "../util/useLoginCheck";
import EditRecipe from "./EditRecipe";
import Spinner from "../util/Spinner";
import { fetchRecipe, deleteRecipe, fetchRecipes } from "../slice/recipeSlice";

const Recipe = () => {
  const dispatch = useDispatch();
  const { recipe, isLoading, error } = useSelector((state) => {
    return state?.recipes;
  });
  const { sid } = useParams();
  const [isUpdateRecipeOpen, setIsUpdateRecipeOpen] = useState(false);
  const [isDeletingRecipe, setIsDeletingRecipe] = useState(false);
  const navigate = useNavigate();

  useLoginCheck();

  const toggleUpdateRecipe = () => {
    setIsUpdateRecipeOpen(!isUpdateRecipeOpen);
  };

  useEffect(() => {
    if (sid) {
      console.log("calling fetch recipe with sid ", sid);
      dispatch(fetchRecipe(sid));
    }
  }, [dispatch, sid]);

  const refreshRecipe = () => {
    if (sid) {
      console.log("refreshing recipe with sid ", sid);
      dispatch(fetchRecipe(sid));
    }
  };

  const deleteRecipeHandler = async (e) => {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this recipe?")) {
      try {
        setIsDeletingRecipe(true);
        dispatch(deleteRecipe(sid)).then(() => {
          setIsDeletingRecipe(false);
          fetchRecipes(); // force a refresh for recipe table
          navigate(`/recipes?refresh=true`);
        });
      } catch (error) {
        console.log("error deleting recipe, message: ", error.message);
      } finally {
        setIsDeletingRecipe(false);
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="flex justify-end gap-2 mb-4">
            <button className="btn-primary" onClick={setIsUpdateRecipeOpen}>
              Edit
            </button>
            <button
              className="btn-secondary"
              disabled={isDeletingRecipe}
              onClick={deleteRecipeHandler}
            >
              {isDeletingRecipe ? "Deleting..." : "Delete"}
            </button>
            {isUpdateRecipeOpen && (
              <EditRecipe
                recipe={recipe}
                closePopup={toggleUpdateRecipe}
                refreshRecipe={refreshRecipe}
              />
            )}
          </div>
          <dl className="card p-4 md:p-6 divide-y divide-stone-200">
            <div className="py-3 first:pt-0">
              <dt className="text-sm font-medium text-stone-500">Recipe</dt>
              <dd className="mt-1 text-stone-800">{recipe?.name}</dd>
            </div>
            <div className="py-3">
              <dt className="text-sm font-medium text-stone-500">Calories</dt>
              <dd className="mt-1 text-stone-800">{recipe?.calories}</dd>
            </div>
            <div className="py-3">
              <dt className="text-sm font-medium text-stone-500">
                From the Kitchen Of
              </dt>
              <dd className="mt-1 text-stone-800">
                {recipe?.fromTheKitchenOf}
              </dd>
            </div>
            <div className="py-3">
              <dt className="text-sm font-medium text-stone-500">
                Total time
              </dt>
              <dd className="mt-1 text-stone-800">{recipe?.totalTime}</dd>
            </div>
            <div className="py-3">
              <dt className="text-sm font-medium text-stone-500">
                Ingredients
              </dt>
              <dd className="mt-1">
                <ul className="list-disc pl-5 space-y-1 text-stone-800">
                  {recipe?.recipeIngredients?.map((recipeIngredient) => {
                    return (
                      <RecipeIngredient
                        key={recipeIngredient.sid}
                        recipeIngredient={recipeIngredient}
                      ></RecipeIngredient>
                    );
                  })}
                </ul>
              </dd>
            </div>
            <div className="py-3 last:pb-0">
              <dt className="text-sm font-medium text-stone-500">
                Instructions
              </dt>
              <dd className="mt-1 text-stone-800 whitespace-pre-wrap">
                {recipe?.instructions}
              </dd>
            </div>
          </dl>
        </>
      )}
    </div>
  );
};
export default Recipe;
